import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/utils/api';
import { useReintentoTrasSesion } from '@/utils/sesion';
import { useAuth } from '@/contexts/AuthContext';
import { 
  parseSafeNumber, 
  parseSafeInt, 
  calculateIMCSafe, 
  calculateHeartRateZonesSafe,
  calculateDoubleProductSafe,
  calculateWaistHeightRatioSafe,
  evaluateAdiposityStatus
} from '@/utils/calculations';
import Layout from '@/components/Layout';
import ControlSinFC from '@/components/ControlSinFC';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  User, 
  FileText, 
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Activity,
  Bone,
  Heart,
  ClipboardList,
  Info,
  Download,
  Sparkles
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { TrialBanner, DownloadBlockOverlay } from '@/components/TrialBanner';
import { evaluateDecisionFE, getDecisionPresentation, intensityUnlock } from '@/utils/decisionState';
import StrengthBlock from '@/components/StrengthBlock';
import { AvisoSinCreditos } from '@/components/SaldoCreditos';
import ElegirAlumnoDialog from '@/components/ElegirAlumnoDialog';
import { buildClinicalInfo } from '@/utils/clinicalInfo';
import { getActivityLabel as formatActivityLevel } from '@/utils/labels';

const STEPS = [
  { id: 1, title: 'Datos Personales', icon: User },
  { id: 2, title: 'Actividad Física', icon: Activity },
  { id: 3, title: 'Factores de Riesgo / Enfermedades', icon: Heart },
  { id: 4, title: 'Signos y Síntomas', icon: AlertTriangle },
  { id: 5, title: 'Limitaciones', icon: Bone },
  { id: 6, title: 'Estudios Complementarios', icon: FileText },
  { id: 7, title: 'Informe Final', icon: ClipboardList },
];

/**
 * Edad en años a partir de la fecha de nacimiento del alumno ("YYYY-MM-DD").
 * Devuelve null si no hay fecha o no es válida, para no pisar lo que el
 * profesional haya cargado a mano.
 */
function edadDesdeNacimiento(iso) {
  if (!iso) return null;
  const nac = new Date(iso);
  if (Number.isNaN(nac.getTime())) return null;
  const hoy = new Date();
  let edad = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad -= 1;
  return edad >= 0 && edad <= 120 ? String(edad) : null;
}

const initialFormData = {
  // Step 1 - Datos Personales
  // Vincula la entrevista con la ficha del alumno para poder seguirlo en el
  // tiempo. Llega por ?alumno=<id> desde el panel de Alumnos.
  alumno_id: null,
  student_name: '',
  age: '',
  sex: '',
  weight: '',
  height: '',
  waist_circumference: '',  // Circunferencia de cintura (cm)
  resting_heart_rate: '',
  blood_pressure_systolic: '',  // mmHg - PAS numérica
  blood_pressure_diastolic: '', // mmHg - PAD numérica
  
  // Step 6 - Estudios Complementarios
  has_lab_recent: false,
  has_ecg: false,
  ecg_result: '', // Sin alteraciones, Alteraciones del segmento ST-T
  has_stress_test: false,
  has_other_studies: false,
  other_studies_types: [], // Holter, Ecocardiograma, Ergometría, Cinecoronariografía, Resonancia cardíaca, TAC coronario
  cholesterol_total: '',
  hdl: '',
  ldl: '',
  triglycerides: '',
  glucose: '',
  
  // Ergometría
  stress_test_date: '',
  stress_test_result: '',
  stress_test_mets: '',
  stress_test_fc_pico: '',
  stress_test_pas_pico: '',  // PAS Pico en ergometría
  stress_test_doble_producto: '',
  stress_test_quiebre_dp: false,
  stress_test_quiebre_dp_valor: '',
  stress_test_vo2_max: '',
  stress_test_sintomas: false,
  stress_test_observaciones: '',
  
  // Step 4 - Signos sugerentes de enfermedad CV/metabólica/renal según ACSM (null = sin seleccionar)
  symptom_chest_pain: null,                  // 1. Dolor o malestar en pecho
  symptom_neck_jaw_arm_pain: null,           // 2. Dolor en cuello, mandíbula o brazos
  symptom_dyspnea: null,                     // 3. Falta de aire inusual o con esfuerzos leves
  symptom_dizziness: null,                   // 4. Mareos o pérdida de conciencia durante actividad física
  symptom_orthopnea: null,                   // 5. Dificultad para respirar acostado o disnea nocturna
  symptom_ankle_swelling: null,              // 6. Hinchazón persistente en tobillos
  symptom_palpitations: null,                // 7. Palpitaciones o latidos irregulares frecuentes
  symptom_intermittent_claudication: null,   // 8. Claudicación intermitente
  symptom_heart_murmur: null,                // 10. Soplo cardíaco informado
  symptom_unusual_fatigue: null,             // 11. Fatiga inusual con actividades habituales
  
  // Step 4 - Actividad Física
  activity_level: '',
  sedentary_hours: '',
  // Estado intermedio del clasificador de actividad (no se envía al backend;
  // solo alimenta el cálculo de activity_level y sobrevive la navegación).
  does_planned_exercise: null,
  act_moderate_intensity: false,
  act_min_30: false,
  act_freq_3: false,
  act_sustained_3mo: false,
  
  // Step 5 - Limitaciones
  has_limitations: null, // null = sin seleccionar
  affected_joint: '',
  limitation_joints: [],
  pain_scale: 0,
  pain_type: '',
  limitation_observations: '',
  
  // Step 3 - Factores de Riesgo / Enfermedades
  // Enfermedades diagnosticadas (checkboxes — false = no marcado)
  cardiac_disease: false,
  cerebrovascular_disease: false,
  peripheral_artery_disease: false,
  chronic_kidney_disease: false,
  // Factores de riesgo (null = sin seleccionar)
  smoking: null,
  hypertension: null,
  hypertension_treatment: null,
  hypertension_medication: '',
  // Betabloqueante: con Sí, el control de intensidad se muestra por Borg y
  // test del habla en vez de zonas de FC. Solo en la entrevista del profesional.
  betabloqueante: null,
  diabetes: null,
  diabetes_treatment: null,
  diabetes_type: '',
  diabetes_medication: '',
  // Prediabetes: factor de riesgo informativo, aparte de la diabetes (enfermedad).
  prediabetes: null,
  dyslipidemia: null,
  dyslipidemia_treatment: null,
  dyslipidemia_medication: '',
  family_history: null,
  // Asignación opcional a grupo (ID del grupo o null = sin grupo)
  group_id: null,
};

// Reference values component
function ReferenceValue({ label, normal, warning }) {
  return (
    <div className="flex items-center gap-1 mt-1">
      <Info className="h-3 w-3 text-slate-400" />
      <span className="text-[11px] text-slate-400">
        {label}: <span className="text-slate-500">{normal}</span>
        {warning && <span className="text-amber-500 ml-1">({warning})</span>}
      </span>
    </div>
  );
}

// Yes/No Toggle Button Component - Supports null state (unselected)
export function YesNoToggle({ value, onChange, id }) {
  return (
    <div className="flex gap-2">
      <Button
        type="button"
        variant={value === true ? "default" : "outline"}
        size="sm"
        className={cn(
          "flex-1 h-10",
          value === true ? "bg-slate-700 hover:bg-slate-800 text-white" : "border-slate-200"
        )}
        onClick={() => onChange(true)}
        data-testid={`${id}-yes`}
      >
        Sí
      </Button>
      <Button
        type="button"
        variant={value === false ? "default" : "outline"}
        size="sm"
        className={cn(
          "flex-1 h-10",
          value === false ? "bg-slate-700 hover:bg-slate-800 text-white" : "border-slate-200"
        )}
        onClick={() => onChange(false)}
        data-testid={`${id}-no`}
      >
        No
      </Button>
    </div>
  );
}

export default function NewEvaluationPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDownloadBlock, setShowDownloadBlock] = useState(false);
  const [groups, setGroups] = useState([]);
  const [alumno, setAlumno] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isTrialUser, refreshUser } = useAuth();

  // Cuentas con pack: si no quedan entrevistas libres, se avisa ANTES de
  // empezar. Si se avisara recien al guardar, el profe perderia toda la
  // entrevista que cargo con el alumno.
  const [saldoAgotado, setSaldoAgotado] = useState(null);
  useEffect(() => {
    let cancelado = false;
    api.getCreditos()
      .then((s) => {
        if (!cancelado && s && s.tipo === 'creditos' && (s.creditos_libres ?? 0) === 0) setSaldoAgotado(s);
      })
      .catch((e) => console.error('[NewEvaluation] no se pudo leer el saldo:', e));
    return () => { cancelado = true; };
  }, []);

  // Si se entra desde el panel de Alumnos (?alumno=<id>), la entrevista queda
  // vinculada a esa ficha y se precargan los datos que ya conocemos de la
  // persona. Evita retipear el nombre y, sobre todo, evita que un error de
  // tipeo genere una ficha duplicada.
  const alumnoIdParam = searchParams.get('alumno');
  useEffect(() => {
    if (!alumnoIdParam) return;
    let cancelled = false;
    api.getAlumno(alumnoIdParam)
      .then((a) => {
        if (cancelled || !a) return;
        setAlumno(a);
        setFormData((prev) => ({
          ...prev,
          alumno_id: a.id,
          student_name: a.nombre || prev.student_name,
          sex: a.sexo || prev.sex,
          phone: a.telefono || prev.phone,
          age: edadDesdeNacimiento(a.fecha_nacimiento) ?? prev.age,
        }));
      })
      .catch(() => {
        toast.error('No se pudo cargar el alumno. La entrevista se va a guardar sin vincular.');
      });
    return () => { cancelled = true; };
  }, [alumnoIdParam]);

  useEffect(() => {
    let cancelled = false;
    api.getGroups().then((data) => {
      if (!cancelled) setGroups(data || []);
    }).catch(() => { /* groups are optional, fail silently */ });
    return () => { cancelled = true; };
  }, []);

  const progress = (currentStep / STEPS.length) * 100;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        // Consentimiento se valida en el gate inicial.
        return formData.student_name && formData.age && formData.sex &&
               formData.weight && formData.height;
      case 2:
        return formData.activity_level;
      case 3:
        // All risk factors must be explicitly answered (not null)
        return formData.smoking !== null &&
               formData.hypertension !== null &&
               formData.diabetes !== null &&
               (formData.diabetes === true || formData.prediabetes !== null) &&
               formData.dyslipidemia !== null &&
               formData.family_history !== null &&
               formData.betabloqueante !== null;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    // La entrevista va a la ficha de un alumno. Si ya hay alguien con ese
    // nombre, antes de guardar se pregunta si es la misma persona. Si no hay
    // nadie, el servidor crea la ficha solo.
    if (!formData.alumno_id && !decisionAlumno.current) {
      let candidatos = [];
      try {
        candidatos = await api.coincidenciasAlumno(formData.student_name);
      } catch (_e) {
        // Sin respuesta: se guarda igual, y el servidor no une a nadie por el nombre.
      }
      if (candidatos.length) {
        setEleccionAlumno({ nombre: formData.student_name, candidatos });
        setLoading(false);
        return;
      }
    }

    try {
      // Map activity level to backend expected values
      const activityLevelMap = {
        'sedentario': 'sedentario',
        'insuficientemente_activa': 'irregular',
        'activa_regular': 'activo_regular'
      };

      // PA: PAS y PAD numéricos (motor compara contra ≥160/100). String "{sbp}/{dbp}"
      // se compone para compat con UI/PDF existente que renderiza la PA legacy.
      const sbp = parseSafeInt(formData.blood_pressure_systolic);
      const dbp = parseSafeInt(formData.blood_pressure_diastolic);
      const bp_string = (sbp !== null && dbp !== null) ? `${sbp}/${dbp}` : null;

      const payload = {
        // Vínculo con la ficha del alumno. Null cuando la entrevista se carga
        // suelta, sin pasar por el panel de Alumnos.
        alumno_id: formData.alumno_id || decisionAlumno.current?.alumno_id || null,
        alumno_nuevo: decisionAlumno.current?.nuevo === true,
        student_name: formData.student_name,
        age: parseSafeInt(formData.age),
        sex: formData.sex,
        weight: parseSafeNumber(formData.weight),
        height: parseSafeNumber(formData.height),
        waist_circumference: parseSafeNumber(formData.waist_circumference),
        resting_heart_rate: parseSafeNumber(formData.resting_heart_rate),
        blood_pressure: bp_string,
        blood_pressure_systolic: sbp,
        blood_pressure_diastolic: dbp,
        has_lab_recent: formData.has_lab_recent || false,
        has_ecg: formData.has_ecg || false,
        has_stress_test: formData.has_stress_test || false,
        has_other_studies: formData.has_other_studies || false,
        cholesterol_total: parseSafeNumber(formData.cholesterol_total),
        hdl: parseSafeNumber(formData.hdl),
        ldl: parseSafeNumber(formData.ldl),
        triglycerides: parseSafeNumber(formData.triglycerides),
        glucose: parseSafeNumber(formData.glucose),
        stress_test_date: formData.stress_test_date || null,
        stress_test_result: formData.stress_test_result || null,
        stress_test_mets: parseSafeNumber(formData.stress_test_mets),
        stress_test_fc_pico: parseSafeInt(formData.stress_test_fc_pico),
        stress_test_doble_producto: parseSafeInt(formData.stress_test_doble_producto),
        stress_test_quiebre_dp: formData.stress_test_quiebre_dp === true,
        stress_test_quiebre_dp_valor: parseSafeInt(formData.stress_test_quiebre_dp_valor),
        stress_test_vo2_max: parseSafeNumber(formData.stress_test_vo2_max),
        stress_test_sintomas: formData.stress_test_sintomas === true,
        stress_test_observaciones: formData.stress_test_observaciones || null,
        symptom_chest_pain: formData.symptom_chest_pain === true,
        symptom_neck_jaw_arm_pain: formData.symptom_neck_jaw_arm_pain === true,
        symptom_dyspnea: formData.symptom_dyspnea === true,
        symptom_dizziness: formData.symptom_dizziness === true,
        symptom_orthopnea: formData.symptom_orthopnea === true,
        symptom_ankle_swelling: formData.symptom_ankle_swelling === true,
        symptom_palpitations: formData.symptom_palpitations === true,
        symptom_intermittent_claudication: formData.symptom_intermittent_claudication === true,
        symptom_heart_murmur: formData.symptom_heart_murmur === true,
        symptom_unusual_fatigue: formData.symptom_unusual_fatigue === true,
        activity_level: activityLevelMap[formData.activity_level] || formData.activity_level,
        // Trazabilidad del clasificador de actividad (aditivo, no afecta al motor)
        does_planned_exercise: formData.does_planned_exercise,
        act_moderate_intensity: formData.act_moderate_intensity === true,
        act_min_30: formData.act_min_30 === true,
        act_freq_3: formData.act_freq_3 === true,
        act_sustained_3mo: formData.act_sustained_3mo === true,
        has_limitations: formData.has_limitations || false,
        affected_joint: formData.has_limitations 
          ? `${(formData.limitation_joints || []).join(', ')}${formData.pain_type ? ` - Dolor ${formData.pain_type}` : ''}${formData.pain_scale > 0 ? ` (${formData.pain_scale}/10)` : ''}${formData.limitation_observations ? ` - ${formData.limitation_observations}` : ''}`
          : null,
        // Enfermedades diagnosticadas (entrada del motor de decisión)
        cardiac_disease: formData.cardiac_disease === true,
        cerebrovascular_disease: formData.cerebrovascular_disease === true,
        peripheral_artery_disease: formData.peripheral_artery_disease === true,
        chronic_kidney_disease: formData.chronic_kidney_disease === true,
        smoking: formData.smoking === true,
        hypertension: formData.hypertension === true,
        hypertension_treatment: formData.hypertension === true ? (formData.hypertension_treatment === true) : null,
        hypertension_medication: formData.hypertension === true && formData.hypertension_treatment === true
          ? ((formData.hypertension_medication || '').trim() || null)
          : null,
        betabloqueante: formData.betabloqueante === true,
        diabetes: formData.diabetes === true,
        diabetes_treatment: formData.diabetes === true ? (formData.diabetes_treatment === true) : null,
        diabetes_type: formData.diabetes === true ? (formData.diabetes_type || null) : null,
        prediabetes: formData.diabetes === true ? false : formData.prediabetes === true,
        family_history: formData.family_history === true,
        dyslipidemia: formData.dyslipidemia === true,
        dyslipidemia_treatment: formData.dyslipidemia === true ? (formData.dyslipidemia_treatment === true) : null,
        group_id: formData.group_id || null,
        // Consentimiento informado (Ley 25.326) — el wizard sólo llega acá
        // si el usuario tildó el gate inicial. Backend completa fecha/IP/versión.
        consent_accepted: true,
      };
      
      // Save evaluation
      let savedEvaluation;
      try {
        savedEvaluation = await api.createEvaluation(payload);
      } catch (saveError) {
        console.error('Error saving evaluation. Raw error:', saveError);
        console.error('Error message:', saveError?.message);
        console.error('Payload sent:', payload);
        const detail = saveError?.message || 'No se pudo guardar la entrevista';
        // Sesión vencida: la ventanita pide la contraseña y, al entrar, se
        // guarda sola con lo que ya está cargado. No se pierde nada.
        if (saveError?.status === 401) {
          marcarPendiente();
          toast.info('Tu sesión se venció. Ingresá de nuevo: lo cargado no se perdió y se guarda solo.');
          setLoading(false);
          return;
        }
        // Sin entrevistas (402). Pasa si se gastó la última en otra pestaña
        // mientras se cargaba esta. La compra se abre en una pestaña NUEVA:
        // en esta queda todo lo cargado, y alcanza con volver a tocar Guardar.
        if (saveError?.status === 402) {
          toast.error(detail, {
            duration: 20000,
            description: 'Tu entrevista no se perdió: comprá en la pestaña nueva y volvé a tocar Guardar acá.',
            action: {
              label: 'Comprar pack',
              onClick: () => window.open('/checkout?producto=pack', '_blank', 'noopener'),
            },
          });
          setLoading(false);
          return;
        }
        toast.error(`Error al guardar la entrevista: ${detail}`);
        setLoading(false);
        return;
      }
      
      toast.success('Entrevista guardada correctamente');
      setLoading(false);
      
      // Redirect to dashboard (more reliable than evaluation detail)
      navigate('/alumnos');
      
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Error: ' + (error.message || 'No se pudo completar la operación'));
      setLoading(false);
    }
  };

  const marcarPendiente = useReintentoTrasSesion(() => handleSubmit());

  // Ficha elegida cuando el nombre coincide con un alumno que ya existe.
  const decisionAlumno = useRef(null);
  const [eleccionAlumno, setEleccionAlumno] = useState(null);
  useEffect(() => { decisionAlumno.current = null; }, [formData.student_name]);
  const elegirFicha = (decision) => {
    decisionAlumno.current = decision;
    setEleccionAlumno(null);
    handleSubmit();
  };

  const renderStep = () => {
    try {
      switch (currentStep) {
        case 1: return <Step1PersonalData formData={formData} updateField={updateField} groups={groups} />;
        case 2: return <Step4Activity formData={formData} updateField={updateField} />;
        case 3: return (
          <>
            <Step6RiskFactors formData={formData} updateField={updateField} />
            <PreguntaBetabloqueante formData={formData} updateField={updateField} />
          </>
        );
        case 4: return <Step3SafetyEvaluation formData={formData} updateField={updateField} />;
        case 5: return <Step5Limitations formData={formData} updateField={updateField} />;
        case 6: return <Step2Studies formData={formData} updateField={updateField} />;
        case 7: return <Step7FinalReport formData={formData} />;
        default: return null;
      }
    } catch (error) {
      console.error('Error rendering step:', error);
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">Error al cargar el paso. Por favor recarga la página.</p>
          <p className="text-xs text-red-600 mt-2">{error.message}</p>
        </div>
      );
    }
  };

  return (
    <Layout>
      {isTrialUser && <TrialBanner className="mb-4" />}
      <ElegirAlumnoDialog
        abierto={!!eleccionAlumno}
        nombre={eleccionAlumno?.nombre}
        candidatos={eleccionAlumno?.candidatos}
        onElegir={(id) => elegirFicha({ alumno_id: id })}
        onNueva={() => elegirFicha({ nuevo: true })}
        onCancelar={() => setEleccionAlumno(null)}
      />
      {showDownloadBlock && <DownloadBlockOverlay onClose={() => { setShowDownloadBlock(false); navigate('/alumnos'); }} />}

      {saldoAgotado ? (
        <AvisoSinCreditos saldo={saldoAgotado} />
      ) : !consent ? (
        <ConsentGate onAccept={() => setConsent(true)} />
      ) : (
      <div className="max-w-4xl mx-auto space-y-6" data-testid="new-evaluation-wizard">

        {/* Header - Minimal */}
        <div className="mb-2">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Nueva Entrevista</p>
        </div>

        {/* Progress */}
        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Progreso del formulario</span>
            <span className="font-medium text-slate-700">{currentStep} de {STEPS.length}</span>
          </div>
          <Progress value={progress} className="h-2" />
          
          {/* Steps Indicator - Desktop */}
          <div className="hidden lg:flex justify-between mt-4">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div 
                  key={step.id}
                  className={cn(
                    "flex items-center gap-2 text-xs",
                    isActive && "text-cyan-600 font-medium",
                    isCompleted && "text-green-600",
                    !isActive && !isCompleted && "text-slate-400"
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors",
                    isActive && "bg-cyan-50 border-cyan-500 text-cyan-600",
                    isCompleted && "bg-green-50 border-green-500 text-green-600",
                    !isActive && !isCompleted && "bg-slate-50 border-slate-200 text-slate-400"
                  )}>
                    {isCompleted ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <span className="hidden xl:inline whitespace-nowrap">{step.title}</span>
                </div>
              );
            })}
          </div>
          
          {/* Mobile Step Indicator */}
          <div className="lg:hidden flex items-center justify-center gap-2 py-2">
            {currentStep >= 1 && currentStep <= STEPS.length && STEPS[currentStep - 1]?.icon && 
              React.createElement(STEPS[currentStep - 1].icon, { className: "h-5 w-5 text-cyan-600" })}
            <span className="font-medium text-slate-700">
              {currentStep >= 1 && currentStep <= STEPS.length ? STEPS[currentStep - 1]?.title || '' : ''}
            </span>
          </div>
        </div>

        {/* Alumno vinculado: deja claro de quién es esta entrevista y si es un retest */}
        {alumno && (
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-sm text-slate-500">Entrevista de</span>
            <span className="font-semibold text-slate-800">{alumno.nombre}</span>
            {alumno.mediciones_count > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600">
                Retest — {alumno.mediciones_count}{' '}
                {alumno.mediciones_count === 1 ? 'entrevista previa' : 'entrevistas previas'}
              </span>
            )}
          </div>
        )}

        {/* Form Card */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <div>
              <CardTitle className="font-['Manrope'] text-slate-900 text-xl">
                {currentStep === 1 ? 'Datos Personales' : 
                 currentStep === 4 ? 'Signos y síntomas de alerta para el inicio de ejercicio físico' : 
                 (currentStep >= 1 && currentStep <= STEPS.length ? STEPS[currentStep - 1]?.title : '')}
              </CardTitle>
              <CardDescription className="text-slate-500 mt-1">
                {currentStep === 1 && "Información básica para el cálculo inicial de riesgo y parámetros fisiológicos."}
                {currentStep === 2 && "Evaluación del nivel de actividad física habitual"}
                {currentStep === 3 && "Enfermedades diagnosticadas y otros factores clínicos relevantes"}
                {currentStep === 4 && "Identificación de condiciones previas"}
                {currentStep === 5 && "Identificación de limitaciones para la prescripción"}
                {currentStep === 6 && "Registre los estudios complementarios disponibles"}
                {currentStep === 7 && "Revise el informe de estratificación antes de finalizar"}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {renderStep()}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-2 pb-24">
          <Button 
            variant="outline" 
            onClick={handlePrev}
            disabled={currentStep === 1}
            className="border-slate-200"
            data-testid="prev-step"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Anterior
          </Button>
          
          {currentStep < STEPS.length ? (
            <Button 
              onClick={handleNext}
              disabled={!validateStep()}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="next-step"
            >
              Siguiente
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={loading}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="submit-evaluation"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Finalizar Entrevista
                </>
              )}
            </Button>
          )}
        </div>
      </div>
      )}
    </Layout>
  );
}

// ==================== CONSENT GATE ====================

function ConsentGate({ onAccept }) {
  const [checked, setChecked] = useState(false);
  return (
    <div className="max-w-2xl mx-auto" data-testid="consent-gate">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50">
          <CardTitle className="font-['Manrope'] text-slate-900 text-xl">
            Consentimiento informado
          </CardTitle>
          <CardDescription className="text-slate-500 mt-1">
            Requerido por la Ley 25.326 de Protección de Datos Personales para iniciar una entrevista.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          <label
            className="flex items-start gap-3 cursor-pointer rounded-lg border border-slate-200 p-4 hover:bg-slate-50 transition-colors"
            data-testid="consent-gate-label"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 accent-[#1F3A5F] flex-shrink-0"
              data-testid="consent-gate-checkbox"
            />
            <span className="text-sm text-slate-700 leading-relaxed">
              Declaro contar con el consentimiento informado del alumno/paciente para registrar
              y procesar sus datos personales y de salud en EVALPRO, con la finalidad exclusiva
              de generar el informe profesional de entrevista previa al ejercicio. El tratamiento
              de datos se realiza conforme a la{' '}
              <a
                href="/privacidad"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-[#1F3A5F] hover:text-[#162a45] font-medium"
                data-testid="consent-gate-privacy-link"
              >
                Política de Privacidad
              </a>{' '}
              y los principios de la Ley 25.326 de Protección de Datos Personales.
            </span>
          </label>

          <div className="flex justify-end pt-2">
            <Button
              onClick={onAccept}
              disabled={!checked}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="consent-gate-start"
            >
              Comenzar entrevista
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== STEP COMPONENTS ====================

export function Step1PersonalData({ formData, updateField, groups = [], hideGroups = false, hideResults = false }) {
  // Calculate IMC in real-time using safe parsing with validation
  const weight = parseSafeNumber(formData.weight);
  const height = parseSafeNumber(formData.height);
  
  // Validate IMC range (prevent absurd values)
  let calculatedIMC = null;
  if (weight && height && height > 50 && height < 300 && weight > 10 && weight < 500) {
    const heightInMeters = height / 100;
    const imc = weight / (heightInMeters * heightInMeters);
    // IMC should be between 10 and 70 for humans
    if (imc >= 10 && imc <= 70) {
      calculatedIMC = imc.toFixed(1);
    }
  }
  
  // Calculate Waist-to-Height Ratio (returns null if waist is 0 or empty)
  const calculatedWHtR = calculateWaistHeightRatioSafe(formData.waist_circumference, formData.height);
  
  // Evaluate adiposity status with all parameters including weight for IMC-based evaluation
  const adiposityStatus = evaluateAdiposityStatus(
    formData.waist_circumference, 
    formData.height, 
    formData.sex,
    formData.weight
  );

  // Check if waist circumference is not available (0 or empty)
  const waistValue = parseSafeNumber(formData.waist_circumference);
  const waistNotAvailable = waistValue === 0;

  // IMC Classification - Solo etiqueta, sin recomendaciones
  const getIMCClassification = (imc) => {
    if (!imc) return null;
    const value = parseSafeNumber(imc);
    if (value === null || value < 10 || value > 70) return null;
    if (value < 18.5) return { label: 'Bajo peso', color: 'text-slate-600' };
    if (value < 25) return { label: 'Normopeso', color: 'text-slate-600' };
    if (value < 30) return { label: 'Sobrepeso', color: 'text-slate-600' };
    if (value < 35) return { label: 'Obesidad Clase I', color: 'text-slate-600' };
    if (value < 40) return { label: 'Obesidad Clase II', color: 'text-slate-600' };
    return { label: 'Obesidad Clase III', color: 'text-slate-600' };
  };

  const imcClassification = getIMCClassification(calculatedIMC);

  return (
    <div className="space-y-6" data-testid="step1-personal-data">
      {/* Required Fields */}
      <div className="space-y-5">
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="student_name" className="text-slate-700 font-medium">
            Nombre y Apellido <span className="text-red-500">*</span>
          </Label>
          <Input
            id="student_name"
            value={formData.student_name}
            onChange={(e) => updateField('student_name', e.target.value)}
            placeholder="Ingrese nombre completo"
            className="border-slate-200 h-11"
            data-testid="input-student_name"
          />
        </div>

        {/* Group assignment (optional) — se oculta en la entrevista pública del alumno */}
        {!hideGroups && (
        <div className="space-y-2">
          <Label htmlFor="group_id" className="text-slate-700 font-medium">
            Asignar a grupo (opcional)
          </Label>
          {groups.length === 0 ? (
            <p className="text-xs text-slate-500 italic" data-testid="no-groups-hint">
              Podés crear grupos desde la sección Grupos del menú.
            </p>
          ) : (
            <Select
              value={formData.group_id || 'none'}
              onValueChange={(v) => updateField('group_id', v === 'none' ? null : v)}
            >
              <SelectTrigger
                id="group_id"
                className="border-slate-200 h-11"
                data-testid="select-group_id"
              >
                <SelectValue placeholder="Sin grupo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin grupo</SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id} data-testid={`select-group-option-${g.id}`}>
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        )}

        {/* Age and Gender Row - better mobile layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Age */}
          <div className="space-y-2">
            <Label htmlFor="age" className="text-slate-700 font-medium">
              Edad <span className="text-red-500">*</span>
            </Label>
            <Input
              id="age"
              type="number"
              inputMode="numeric"
              value={formData.age}
              onChange={(e) => updateField('age', e.target.value)}
              placeholder="Años"
              min="1"
              max="120"
              className="border-slate-200 h-11 w-full sm:w-32"
              data-testid="input-age"
            />
            <p className="text-xs text-slate-400">Se utiliza para el cálculo de frecuencia cardíaca de referencia y estratificación.</p>
          </div>

          {/* Gender - Radio buttons */}
          <div className="space-y-3">
            <Label className="text-slate-700 font-medium">
              Género <span className="text-red-500">*</span>
            </Label>
            <RadioGroup 
              value={formData.sex} 
              onValueChange={(v) => updateField('sex', v)}
              className="flex flex-col space-y-2"
            >
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="Masculino" id="gender-male" data-testid="radio-gender-male" />
                <Label htmlFor="gender-male" className="text-slate-600 font-normal cursor-pointer">Masculino</Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="Femenino" id="gender-female" data-testid="radio-gender-female" />
                <Label htmlFor="gender-female" className="text-slate-600 font-normal cursor-pointer">Femenino</Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="No binario" id="gender-nonbinary" data-testid="radio-gender-nonbinary" />
                <Label htmlFor="gender-nonbinary" className="text-slate-600 font-normal cursor-pointer">No binario</Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="Prefiero no responder" id="gender-noresponse" data-testid="radio-gender-noresponse" />
                <Label htmlFor="gender-noresponse" className="text-slate-600 font-normal cursor-pointer">Prefiero no responder</Label>
              </div>
            </RadioGroup>
            {(formData.sex === 'No binario' || formData.sex === 'Prefiero no responder') && (
              <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                En caso de no especificar género masculino o femenino, no se aplican criterios de riesgo asociados a edad según clasificación tradicional.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Body Composition Section */}
      <div className="pt-4">
        <div className="mb-4">
          <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Composición Corporal y Adiposidad</h4>
          <p className="text-xs text-slate-400 mt-1">Evaluación de adiposidad basada en evidencia científica</p>
        </div>
        
        <div className="space-y-5">
          {/* Weight and Height Row - side by side */}
          <div className="grid grid-cols-2 gap-4">
            {/* Weight */}
            <div className="space-y-2">
              <Label htmlFor="weight" className="text-slate-700 font-medium">
                Peso (kg) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="weight"
                type="number"
                inputMode="decimal"
                step="0.1"
                value={formData.weight}
                onChange={(e) => updateField('weight', e.target.value)}
                placeholder="Ej: 75.5"
                className="border-slate-200 h-11 w-full"
                data-testid="input-weight"
              />
            </div>

            {/* Height */}
            <div className="space-y-2">
              <Label htmlFor="height" className="text-slate-700 font-medium">
                Altura (cm) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="height"
                type="number"
                inputMode="numeric"
                value={formData.height}
                onChange={(e) => updateField('height', e.target.value)}
                placeholder="Ej: 175"
                className="border-slate-200 h-11 w-full"
                data-testid="input-height"
              />
            </div>
          </div>

          {/* Waist Circumference */}
          <div className="space-y-2">
            <Label htmlFor="waist_circumference" className="text-slate-700 font-medium">
              Circunferencia de Cintura (cm)
            </Label>
            <Input
              id="waist_circumference"
              type="number"
              step="0.1"
              value={formData.waist_circumference}
              onChange={(e) => updateField('waist_circumference', e.target.value)}
              placeholder="Ej: 85"
              className="border-slate-200 h-11 w-32"
              data-testid="input-waist_circumference"
            />
            <div className="text-xs text-slate-500 mt-2 space-y-2">
              <div className="bg-slate-50 p-2 rounded border border-slate-100">
                <p className="font-medium text-slate-600 mb-1">Valores de referencia (riesgo aumentado):</p>
                <ul className="list-disc list-inside space-y-0.5 pl-1">
                  <li>Hombres: ≥102 cm</li>
                  <li>Mujeres: ≥88 cm</li>
                </ul>
              </div>
              <p className="text-slate-400 italic">
                <Info className="h-3 w-3 inline mr-1" />
                Según evidencia internacional, la circunferencia de cintura debe medirse en posición de pie, al final de una espiración normal, en el punto medio entre el borde inferior de la última costilla y la cresta ilíaca.
              </p>
            </div>
          </div>

          {/* Body Composition Results - se ocultan en la entrevista pública del alumno */}
          {!hideResults && calculatedIMC && (
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-4">
              <h5 className="text-sm font-semibold text-slate-700">Resultados de Composición Corporal</h5>
              
              {/* IMC */}
              <div className="flex items-center justify-between py-2 border-b border-slate-200">
                <div>
                  <span className="text-sm text-slate-600">IMC:</span>
                  {imcClassification && (
                    <span className={cn("text-xs ml-2", imcClassification.color)}>
                      ({imcClassification.label})
                    </span>
                  )}
                </div>
                <span className="text-lg font-semibold text-slate-800">{calculatedIMC} kg/m²</span>
              </div>
              
              {/* Waist Circumference - Show "not available" when value is 0 */}
              {waistNotAvailable ? (
                <div className="flex items-center justify-between py-2 border-b border-slate-200">
                  <span className="text-sm text-slate-600">Circunferencia de cintura:</span>
                  <span className="text-sm text-slate-400 italic">no disponible</span>
                </div>
              ) : calculatedWHtR && (
                <>
                  {/* Waist-to-Height Ratio */}
                  <div className="flex items-center justify-between py-2 border-b border-slate-200">
                    <div>
                      <span className="text-sm text-slate-600">Relación Cintura/Altura:</span>
                      <span className={cn("text-xs ml-2", parseFloat(calculatedWHtR) >= 0.50 ? 'text-amber-600' : 'text-green-600')}>
                        ({parseFloat(calculatedWHtR) >= 0.50 ? 'Adiposidad central aumentada' : 'Normal'})
                      </span>
                    </div>
                    <span className="text-lg font-semibold text-slate-800">{calculatedWHtR}</span>
                  </div>
                </>
              )}
              
              {/* Adiposity Interpretation - ONLY show when waist IS available */}
              {!waistNotAvailable && adiposityStatus && (
                <div className={cn(
                  "p-3 rounded-xl mt-2 overflow-hidden",
                  adiposityStatus.status === 'normal' ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'
                )}>
                  <p className={cn("text-sm font-medium leading-relaxed break-words", adiposityStatus.color)}>
                    {adiposityStatus.description}
                  </p>
                  {adiposityStatus.secondaryDescription && (
                    <p className={cn("text-sm mt-2 leading-relaxed break-words", adiposityStatus.color)}>
                      {adiposityStatus.secondaryDescription}
                    </p>
                  )}
                </div>
              )}
              
              {/* Disclaimer */}
              <p className="text-xs text-slate-400 italic mt-2">
                Esta información es orientativa y no constituye diagnóstico médico.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Optional Fields Section */}
      <div className="pt-4">
        <div className="mb-3">
          <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Signos Vitales en Reposo (Opcional)</h4>
          <p className="text-xs text-slate-400 mt-1">Se recomienda realizar al menos 3 mediciones y utilizar el valor promedio.</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Resting Heart Rate */}
          <div className="space-y-2">
            <Label htmlFor="resting_heart_rate" className="text-slate-700 font-medium">
              Frecuencia Cardíaca en Reposo (lpm)
            </Label>
            <Input
              id="resting_heart_rate"
              type="number"
              value={formData.resting_heart_rate}
              onChange={(e) => updateField('resting_heart_rate', e.target.value)}
              placeholder="Ej: 72"
              className="border-slate-200 h-11 w-32"
              data-testid="input-resting_heart_rate"
            />
            <p className="text-xs text-slate-400">Valores habituales en reposo adulto: 60–100 lpm.</p>
          </div>

          {/* Blood Pressure - PAS y PAD por separado (numéricos para motor) */}
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">
              Presión Arterial en Reposo (mmHg)
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="blood_pressure_systolic"
                type="number"
                inputMode="numeric"
                min="50"
                max="300"
                value={formData.blood_pressure_systolic}
                onChange={(e) => updateField('blood_pressure_systolic', e.target.value)}
                placeholder="PAS"
                className="border-slate-200 h-11 w-24"
                data-testid="input-blood_pressure_systolic"
                aria-label="Presión arterial sistólica"
              />
              <span className="text-slate-400 font-medium text-lg">/</span>
              <Input
                id="blood_pressure_diastolic"
                type="number"
                inputMode="numeric"
                min="30"
                max="200"
                value={formData.blood_pressure_diastolic}
                onChange={(e) => updateField('blood_pressure_diastolic', e.target.value)}
                placeholder="PAD"
                className="border-slate-200 h-11 w-24"
                data-testid="input-blood_pressure_diastolic"
                aria-label="Presión arterial diastólica"
              />
              <span className="text-slate-500 text-sm ml-1">mmHg</span>
            </div>
            <div className="text-xs text-slate-500 mt-2 space-y-1">
              <p className="font-medium text-slate-600">Clasificación orientativa:</p>
              <ul className="list-disc list-inside space-y-0.5 pl-1">
                <li>Normal: &lt;120 y &lt;80 mmHg</li>
                <li>Elevada: 120–129 y &lt;80 mmHg</li>
                <li>Hipertensión Estadio 1: 130–139 o 80–89 mmHg</li>
                <li>Hipertensión Estadio 2: ≥140 o ≥90 mmHg</li>
              </ul>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

function Step2Studies({ formData, updateField }) {
  // Debug log
  console.log('Step2Studies rendering, formData:', formData);
  
  // Ensure boolean values for checkboxes
  const hasLabRecent = formData.has_lab_recent === true;
  const hasEcg = formData.has_ecg === true;
  const hasStressTest = formData.has_stress_test === true;
  const hasOtherStudies = formData.has_other_studies === true;
  
  return (
    <div className="space-y-6" data-testid="step2-studies">
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <Info className="h-4 w-4 inline mr-2" />
          Marque los estudios complementarios disponibles. Los valores de laboratorio se utilizan para la entrevista.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className={cn(
          "flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors",
          hasLabRecent ? "border-cyan-500 bg-cyan-50" : "border-slate-200 hover:bg-slate-50"
        )}>
          <Checkbox
            id="has_lab_recent"
            checked={hasLabRecent}
            onCheckedChange={(v) => updateField('has_lab_recent', v === true)}
            data-testid="checkbox-has_lab_recent"
          />
          <div>
            <span className="font-medium text-slate-700">Laboratorio Reciente</span>
            <p className="text-xs text-slate-500">Análisis de sangre (últimos 6 meses)</p>
          </div>
        </label>
        
        <label className={cn(
          "flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors",
          hasEcg ? "border-cyan-500 bg-cyan-50" : "border-slate-200 hover:bg-slate-50"
        )}>
          <Checkbox
            id="has_ecg"
            checked={hasEcg}
            onCheckedChange={(v) => updateField('has_ecg', v === true)}
            data-testid="checkbox-has_ecg"
          />
          <div>
            <span className="font-medium text-slate-700">Electrocardiograma</span>
            <p className="text-xs text-slate-500">ECG en reposo</p>
          </div>
        </label>
        
        <label className={cn(
          "flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors",
          hasStressTest ? "border-cyan-500 bg-cyan-50" : "border-slate-200 hover:bg-slate-50"
        )}>
          <Checkbox
            id="has_stress_test"
            checked={hasStressTest}
            onCheckedChange={(v) => updateField('has_stress_test', v === true)}
            data-testid="checkbox-has_stress_test"
          />
          <div>
            <span className="font-medium text-slate-700">Ergometría / PEG</span>
            <p className="text-xs text-slate-500">Prueba de esfuerzo graduado</p>
          </div>
        </label>
        
        <label className={cn(
          "flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors",
          hasOtherStudies ? "border-cyan-500 bg-cyan-50" : "border-slate-200 hover:bg-slate-50"
        )}>
          <Checkbox
            id="has_other_studies"
            checked={hasOtherStudies}
            onCheckedChange={(v) => updateField('has_other_studies', v === true)}
            data-testid="checkbox-has_other_studies"
          />
          <div>
            <span className="font-medium text-slate-700">Otros Estudios</span>
            <p className="text-xs text-slate-500">Ecocardiograma, Holter, etc.</p>
          </div>
        </label>
      </div>
      
      {/* ECG Options */}
      {hasEcg && (
        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
          <h4 className="font-semibold text-slate-700 text-sm">Resultado del ECG</h4>
          <div className="flex flex-wrap gap-2">
            {['Sin alteraciones', 'Alteraciones del segmento ST-T'].map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => updateField('ecg_result', option)}
                className={cn(
                  "px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  formData.ecg_result === option
                    ? "bg-cyan-500 text-white"
                    : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"
                )}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Other Studies Options */}
      {hasOtherStudies && (
        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
          <h4 className="font-semibold text-slate-700 text-sm">Seleccionar estudios realizados</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {['Holter', 'Ecocardiograma', 'Ergometría', 'Cinecoronariografía', 'Resonancia cardíaca', 'TAC coronario'].map((study) => (
              <label
                key={study}
                className={cn(
                  "flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer transition-colors text-sm min-h-[44px] overflow-hidden",
                  (formData.other_studies_types || []).includes(study)
                    ? "bg-cyan-500 text-white"
                    : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"
                )}
              >
                <Checkbox
                  checked={(formData.other_studies_types || []).includes(study)}
                  onCheckedChange={(checked) => {
                    const current = formData.other_studies_types || [];
                    if (checked) {
                      updateField('other_studies_types', [...current, study]);
                    } else {
                      updateField('other_studies_types', current.filter(s => s !== study));
                    }
                  }}
                  className="border-current"
                />
                {study}
              </label>
            ))}
          </div>
        </div>
      )}
      
      {hasLabRecent && (
        <div className="p-5 bg-slate-50 rounded-lg border border-slate-200 space-y-5" data-testid="lab-values-block">
          <h4 className="font-semibold text-slate-700">Valores de Laboratorio</h4>

          {/* Perfil Lipídico */}
          <div className="space-y-3">
            <h5 className="text-sm font-semibold text-slate-600 border-b border-slate-200 pb-1">Perfil lipídico</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="cholesterol_total" className="text-slate-700 font-medium">Colesterol total (mg/dl)</Label>
                <Input
                  id="cholesterol_total"
                  type="number"
                  value={formData.cholesterol_total}
                  onChange={(e) => updateField('cholesterol_total', e.target.value)}
                  placeholder="190"
                  className="border-slate-200 h-11"
                  data-testid="input-cholesterol_total"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="hdl" className="text-slate-700 font-medium">HDL (mg/dl)</Label>
                <Input
                  id="hdl"
                  type="number"
                  value={formData.hdl}
                  onChange={(e) => updateField('hdl', e.target.value)}
                  placeholder="55"
                  className="border-slate-200 h-11"
                  data-testid="input-hdl"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ldl" className="text-slate-700 font-medium">LDL (mg/dl)</Label>
                <Input
                  id="ldl"
                  type="number"
                  value={formData.ldl}
                  onChange={(e) => updateField('ldl', e.target.value)}
                  placeholder="110"
                  className="border-slate-200 h-11"
                  data-testid="input-ldl"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="triglycerides" className="text-slate-700 font-medium">Triglicéridos (mg/dl)</Label>
                <Input
                  id="triglycerides"
                  type="number"
                  value={formData.triglycerides}
                  onChange={(e) => updateField('triglycerides', e.target.value)}
                  placeholder="130"
                  className="border-slate-200 h-11"
                  data-testid="input-triglycerides"
                />
              </div>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Col. total: &lt;200 · LDL: &lt;100 · HDL: &gt;40 (H) / &gt;50 (M) · Triglicéridos: &lt;150 mg/dl
            </p>
          </div>

          {/* Glucemia */}
          <div className="space-y-3">
            <h5 className="text-sm font-semibold text-slate-600 border-b border-slate-200 pb-1">Glucemia</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="glucose" className="text-slate-700 font-medium">Glucemia basal (mg/dl)</Label>
                <Input
                  id="glucose"
                  type="number"
                  value={formData.glucose}
                  onChange={(e) => updateField('glucose', e.target.value)}
                  placeholder="95"
                  className="border-slate-200 h-11"
                  data-testid="input-glucose"
                />
              </div>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Normal: &lt;100 · Prediabetes: 100–125 · Diabetes: ≥126 mg/dl
            </p>
          </div>
        </div>
      )}
      
      {hasStressTest && (
        <div className="p-5 bg-slate-50 rounded-lg border border-slate-200 space-y-4">
          <h4 className="font-semibold text-slate-700">Datos de Ergometría / Prueba de Esfuerzo</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="stress_test_date" className="text-slate-700">Fecha del Estudio</Label>
              <Input
                id="stress_test_date"
                type="date"
                value={formData.stress_test_date}
                onChange={(e) => updateField('stress_test_date', e.target.value)}
                className="border-slate-200 h-11"
                data-testid="input-stress_test_date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">Resultado</Label>
              <Select value={formData.stress_test_result} onValueChange={(v) => updateField('stress_test_result', v)}>
                <SelectTrigger className="border-slate-200 h-11" data-testid="select-stress_test_result">
                  <SelectValue placeholder="Seleccionar resultado..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal / Negativo</SelectItem>
                  <SelectItem value="alteraciones">Con alteraciones / Positivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="stress_test_mets" className="text-slate-700">Capacidad Funcional (METs)</Label>
              <Input
                id="stress_test_mets"
                type="number"
                step="0.1"
                value={formData.stress_test_mets}
                onChange={(e) => updateField('stress_test_mets', e.target.value)}
                placeholder="10.5"
                className="border-slate-200 h-11"
                data-testid="input-stress_test_mets"
              />
              <ReferenceValue label="Buena capacidad" normal=">10 METs" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="stress_test_fc_pico" className="text-slate-700">FC Pico Alcanzada (lpm)</Label>
              <Input
                id="stress_test_fc_pico"
                type="number"
                value={formData.stress_test_fc_pico}
                onChange={(e) => updateField('stress_test_fc_pico', e.target.value)}
                placeholder="175"
                className="border-slate-200 h-11"
                data-testid="input-stress_test_fc_pico"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="stress_test_pas_pico" className="text-slate-700">PAS Pico (mmHg)</Label>
              <Input
                id="stress_test_pas_pico"
                type="number"
                value={formData.stress_test_pas_pico}
                onChange={(e) => updateField('stress_test_pas_pico', e.target.value)}
                placeholder="180"
                className="border-slate-200 h-11"
                data-testid="input-stress_test_pas_pico"
              />
              <p className="text-xs text-slate-400">PAS máxima alcanzada en esfuerzo.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="stress_test_vo2_max" className="text-slate-700">VO₂ Máx (ml/kg/min)</Label>
              <Input
                id="stress_test_vo2_max"
                type="number"
                step="0.1"
                value={formData.stress_test_vo2_max}
                onChange={(e) => updateField('stress_test_vo2_max', e.target.value)}
                placeholder="35.5"
                className="border-slate-200 h-11"
                data-testid="input-stress_test_vo2_max"
              />
            </div>
          </div>
          
          {/* Doble Producto Pico - Calculado automáticamente */}
          {formData.stress_test_fc_pico && formData.stress_test_pas_pico && (
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Doble Producto Pico:</span>
                <span className="text-lg font-bold font-mono text-slate-800">
                  {calculateDoubleProductSafe(formData.stress_test_fc_pico, formData.stress_test_pas_pico) || '—'}
                </span>
              </div>
            </div>
          )}
          
          {/* Quiebre del Doble Producto */}
          <div className="space-y-3">
            <Label className="text-slate-700 font-medium">¿Se observó quiebre del doble producto?</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={formData.stress_test_quiebre_dp === true ? "default" : "outline"}
                size="sm"
                className={cn(
                  "flex-1 h-10",
                  formData.stress_test_quiebre_dp === true ? "bg-slate-700 hover:bg-slate-800 text-white" : "border-slate-200"
                )}
                onClick={() => updateField('stress_test_quiebre_dp', true)}
                data-testid="stress_test_quiebre_dp-yes"
              >
                Sí
              </Button>
              <Button
                type="button"
                variant={formData.stress_test_quiebre_dp === false ? "default" : "outline"}
                size="sm"
                className={cn(
                  "flex-1 h-10",
                  formData.stress_test_quiebre_dp === false ? "bg-slate-700 hover:bg-slate-800 text-white" : "border-slate-200"
                )}
                onClick={() => {
                  updateField('stress_test_quiebre_dp', false);
                  updateField('stress_test_quiebre_dp_valor', '');
                }}
                data-testid="stress_test_quiebre_dp-no"
              >
                No
              </Button>
            </div>
            {formData.stress_test_quiebre_dp === true && (
              <div className="space-y-2">
                <Label htmlFor="stress_test_quiebre_dp_valor" className="text-slate-700">
                  Valor del quiebre (mmHg·lpm)
                </Label>
                <Input
                  id="stress_test_quiebre_dp_valor"
                  type="number"
                  value={formData.stress_test_quiebre_dp_valor}
                  onChange={(e) => updateField('stress_test_quiebre_dp_valor', e.target.value)}
                  placeholder="Ej: 25000"
                  className="border-slate-200 h-11 w-40"
                  data-testid="input-stress_test_quiebre_dp_valor"
                />
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-3 pt-2">
            <Checkbox
              id="stress_test_sintomas"
              checked={formData.stress_test_sintomas}
              onCheckedChange={(v) => updateField('stress_test_sintomas', v)}
              data-testid="checkbox-stress_test_sintomas"
            />
            <Label htmlFor="stress_test_sintomas" className="cursor-pointer text-slate-700">
              Presentó síntomas durante la prueba
            </Label>
          </div>
        </div>
      )}
    </div>
  );
}

export function Step3SafetyEvaluation({ formData, updateField, hideMedicalAlert = false }) {
  const questions = [
    { 
      id: 'symptom_chest_pain', 
      question: '¿Ha experimentado dolor o malestar en el pecho durante reposo o actividad física?'
    },
    { 
      id: 'symptom_neck_jaw_arm_pain', 
      question: '¿Siente dolor o molestia en el cuello, mandíbula o brazos durante actividad o reposo?'
    },
    { 
      id: 'symptom_dyspnea', 
      question: '¿Presenta falta de aire inusual o con esfuerzos leves?'
    },
    { 
      id: 'symptom_dizziness', 
      question: '¿Ha tenido mareos o pérdida de conciencia durante actividad física?'
    },
    { 
      id: 'symptom_orthopnea', 
      question: '¿Tiene dificultad para respirar al estar acostado o episodios de falta de aire nocturna?'
    },
    { 
      id: 'symptom_ankle_swelling', 
      question: '¿Presenta hinchazón persistente en los tobillos?'
    },
    { 
      id: 'symptom_palpitations', 
      question: '¿Percibe palpitaciones o latidos irregulares frecuentes?'
    },
    { 
      id: 'symptom_intermittent_claudication', 
      question: '¿Experimenta claudicación intermitente (dolor en piernas al caminar que mejora con reposo)?'
    },
    { 
      id: 'symptom_heart_murmur', 
      question: '¿Le han informado que tiene un soplo cardíaco?'
    },
    { 
      id: 'symptom_unusual_fatigue', 
      question: '¿Experimenta fatiga inusual con actividades habituales?'
    },
  ];

  // Check if any answer is "Yes"
  const hasAnyPositive = questions.some(q => formData[q.id] === true);
  
  return (
    <div className="space-y-6" data-testid="step3-safety-evaluation">
      {/* Header info */}
      <div className="space-y-2">
        <p className="text-sm text-slate-600">
          Este apartado permite identificar signos y síntomas que podrían requerir evaluación médica previa antes de iniciar un programa estructurado de ejercicio físico.
        </p>
        <p className="text-xs text-slate-400 italic">
          Basado en consensos y guías internacionales.
        </p>
      </div>
      
      {/* Questions */}
      <div className="space-y-4">
        {questions.map((q, index) => (
          <div key={q.id} className="p-5 border border-slate-200 rounded-lg bg-white">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-semibold text-slate-600 flex-shrink-0">
                {index + 1}
              </div>
              <div className="flex-1 space-y-3">
                <p className="font-medium text-slate-800 leading-relaxed">{q.question}</p>
                <YesNoToggle
                  value={formData[q.id]}
                  onChange={(v) => updateField(q.id, v)}
                  id={q.id}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Info banner when any answer is Yes - se oculta en la entrevista pública del alumno */}
      {!hideMedicalAlert && hasAnyPositive && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <p className="text-sm text-emerald-800">
            De acuerdo con la información consignada, se sugiere realizar evaluación médica previa antes de iniciar un programa estructurado de ejercicio físico.
          </p>
        </div>
      )}
    </div>
  );
}

export function Step4Activity({ formData, updateField }) {
  // Clasificador todo-o-nada (reemplaza al radio de 3 niveles OMS).
  // Paso 1: ¿hace ejercicio planificado? Paso 2 (solo si "Sí"): 4 criterios.
  // Salida canónica que consume el motor: sedentario | irregular | activo_regular.
  const ACTIVITY_CRITERIA = [
    { key: 'act_moderate_intensity', label: 'Es de intensidad moderada o superior' },
    { key: 'act_min_30', label: 'Dura al menos 30 minutos por sesión' },
    { key: 'act_freq_3', label: 'Lo realiza al menos 3 veces por semana' },
    { key: 'act_sustained_3mo', label: 'Lo sostiene desde hace al menos 3 meses' },
  ];

  const doesExercise = formData.does_planned_exercise; // null | true | false

  const computeLevel = (does, checks) => {
    if (does === false) return 'sedentario';
    if (does === true) {
      return ACTIVITY_CRITERIA.every((c) => checks[c.key] === true)
        ? 'activo_regular'
        : 'irregular';
    }
    return '';
  };

  const handleDoesExercise = (value) => {
    updateField('does_planned_exercise', value);
    const checks = {};
    ACTIVITY_CRITERIA.forEach((c) => {
      // Al responder "No" se limpia el checklist. Al responder "Sí" se conservan
      // las marcas previas (salvo que antes se hubiera respondido "No", que ya las limpió).
      const v = value === false ? false : !!formData[c.key];
      checks[c.key] = v;
      if (value === false) updateField(c.key, false);
    });
    updateField('activity_level', computeLevel(value, checks));
  };

  const toggleCriterion = (key, checked) => {
    const isChecked = checked === true;
    updateField(key, isChecked);
    const checks = {};
    ACTIVITY_CRITERIA.forEach((c) => {
      checks[c.key] = c.key === key ? isChecked : !!formData[c.key];
    });
    updateField('activity_level', computeLevel(formData.does_planned_exercise, checks));
  };

  return (
    <div className="space-y-6" data-testid="step4-activity">
      <div className="space-y-3">
        <Label className="text-slate-700 font-medium">
          ¿Actualmente realiza ejercicio físico planificado? <span className="text-red-500">*</span>
        </Label>
        <RadioGroup
          value={doesExercise === true ? 'si' : doesExercise === false ? 'no' : ''}
          onValueChange={(v) => handleDoesExercise(v === 'si')}
          className="grid grid-cols-2 gap-3"
        >
          {[
            { value: 'no', label: 'No' },
            { value: 'si', label: 'Sí' },
          ].map((opt) => {
            const selected =
              (opt.value === 'si' && doesExercise === true) ||
              (opt.value === 'no' && doesExercise === false);
            return (
              <label
                key={opt.value}
                className={cn(
                  'flex items-center justify-center gap-2 p-4 border rounded-lg cursor-pointer transition-colors font-medium',
                  selected
                    ? 'border-cyan-500 bg-cyan-50 text-cyan-800'
                    : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                )}
              >
                <RadioGroupItem
                  value={opt.value}
                  id={`does-exercise-${opt.value}`}
                  data-testid={`radio-does-exercise-${opt.value}`}
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </RadioGroup>
      </div>

      {doesExercise === true && (
        <div className="space-y-3" data-testid="activity-checklist">
          <Label className="text-slate-700 font-medium">
            Marcá todas las que correspondan a ese ejercicio:
          </Label>
          <p className="text-xs text-slate-500">
            Debe cumplir las 4 para clasificar como activo/a regular.
          </p>
          <div className="space-y-2">
            {ACTIVITY_CRITERIA.map((c) => {
              const checked = !!formData[c.key];
              return (
                <label
                  key={c.key}
                  htmlFor={`activity-${c.key}`}
                  className={cn(
                    'flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors',
                    checked ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200 hover:bg-slate-50'
                  )}
                >
                  <Checkbox
                    id={`activity-${c.key}`}
                    checked={checked}
                    onCheckedChange={(v) => toggleCriterion(c.key, v)}
                    data-testid={`checkbox-${c.key}`}
                    className="h-5 w-5 rounded-[4px] border-2 border-slate-400 data-[state=checked]:border-cyan-600 data-[state=checked]:bg-cyan-600"
                  />
                  <span className="text-sm text-slate-800">{c.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      <Separator />

      <div className="space-y-3">
        <Label htmlFor="sedentary_hours" className="text-slate-700 font-medium">
          Horas de Conducta Sedentaria por Día
        </Label>
        <p className="text-xs text-slate-500">
          Tiempo aproximado que pasa sentado/acostado despierto (trabajo, transporte, ocio).
        </p>
        <div className="flex items-center gap-4">
          <Input
            id="sedentary_hours"
            type="number"
            value={formData.sedentary_hours}
            onChange={(e) => updateField('sedentary_hours', e.target.value)}
            placeholder="Ej: 8"
            min="0"
            max="24"
            className="border-slate-200 h-11 w-32"
            data-testid="input-sedentary_hours"
          />
          <span className="text-slate-500">horas/día</span>
        </div>
      </div>
    </div>
  );
}

export function Step5Limitations({ formData, updateField }) {
  const joints = [
    { id: 'cervical', label: 'Columna Cervical' },
    { id: 'hombro', label: 'Hombro' },
    { id: 'codo', label: 'Codo' },
    { id: 'muneca', label: 'Muñeca/Mano' },
    { id: 'dorsal', label: 'Columna Dorsal' },
    { id: 'lumbar', label: 'Columna Lumbar' },
    { id: 'cadera', label: 'Cadera' },
    { id: 'rodilla', label: 'Rodilla' },
    { id: 'tobillo', label: 'Tobillo/Pie' },
  ];

  const painTypes = [
    { value: 'mecanico', label: 'Mecánico', description: 'Aumenta con el movimiento/carga, mejora con reposo' },
    { value: 'inflamatorio', label: 'Inflamatorio', description: 'Rigidez matutina >30min, mejora con movimiento' },
    { value: 'cronico', label: 'Crónico', description: 'Presente >3 meses, puede ser constante' },
    { value: 'agudo', label: 'Agudo', description: 'Inicio reciente, relacionado con evento específico' },
  ];

  const toggleJoint = (jointId) => {
    const currentJoints = formData.limitation_joints || [];
    const newJoints = currentJoints.includes(jointId)
      ? currentJoints.filter(j => j !== jointId)
      : [...currentJoints, jointId];
    updateField('limitation_joints', newJoints);
  };
  
  return (
    <div className="space-y-6">
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <Info className="h-4 w-4 inline mr-2" />
          Identifique limitaciones que afecten la prescripción de ejercicio. 
          Esta información es fundamental para adaptar el programa de entrenamiento.
        </p>
      </div>

      <div className="space-y-3">
        <Label className="text-slate-700 font-medium">¿Presenta limitaciones osteoarticulares?</Label>
        <YesNoToggle
          value={formData.has_limitations}
          onChange={(v) => updateField('has_limitations', v)}
          id="has_limitations"
        />
      </div>
      
      {formData.has_limitations && (
        <div className="space-y-6 p-5 bg-slate-50 rounded-lg border border-slate-200">
          {/* Joint Selection */}
          <div className="space-y-3">
            <Label className="text-slate-700 font-medium">Articulaciones/Zonas Afectadas</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {joints.map((joint) => (
                <button
                  key={joint.id}
                  type="button"
                  onClick={() => toggleJoint(joint.id)}
                  className={cn(
                    "p-2.5 text-sm border rounded-lg transition-colors text-center min-h-[52px] flex items-center justify-center whitespace-normal break-words",
                    (formData.limitation_joints || []).includes(joint.id)
                      ? "bg-cyan-100 border-cyan-500 text-cyan-800"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  )}
                >
                  {joint.label}
                </button>
              ))}
            </div>
          </div>

          {/* Pain Scale */}
          <div className="space-y-3">
            <div className="flex justify-between">
              <Label className="text-slate-700 font-medium">Escala de Dolor (EVA)</Label>
              <span className="text-lg font-bold text-slate-800">{formData.pain_scale || 0}/10</span>
            </div>
            <Slider
              value={[formData.pain_scale || 0]}
              onValueChange={([v]) => updateField('pain_scale', v)}
              max={10}
              step={1}
              className="py-4 mb-4"
            />
            <div className="flex justify-between text-xs text-slate-500">
              <span>Sin dolor</span>
              <span>Dolor leve</span>
              <span>Moderado</span>
              <span>Severo</span>
              <span>Máximo</span>
            </div>
          </div>

          {/* Pain Type */}
          <div className="space-y-3">
            <Label className="text-slate-700 font-medium">Tipo de Dolor</Label>
            <RadioGroup 
              value={formData.pain_type} 
              onValueChange={(v) => updateField('pain_type', v)}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
            >
              {painTypes.map((type) => (
                <label
                  key={type.value}
                  className={cn(
                    "flex items-start space-x-2 p-3 border rounded-xl cursor-pointer transition-colors overflow-hidden",
                    formData.pain_type === type.value 
                      ? "border-cyan-500 bg-cyan-50" 
                      : "border-slate-200 hover:bg-white"
                  )}
                >
                  <RadioGroupItem value={type.value} id={type.value} className="mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <span className="font-medium text-slate-700 text-sm">{type.label}</span>
                    <p className="text-xs text-slate-500 break-words leading-relaxed">{type.description}</p>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          {/* Observations */}
          <div className="space-y-2">
            <Label htmlFor="limitation_observations" className="text-slate-700 font-medium">
              Observaciones Clínicas
            </Label>
            <Textarea
              id="limitation_observations"
              value={formData.limitation_observations}
              onChange={(e) => updateField('limitation_observations', e.target.value)}
              placeholder="Diagnóstico específico, tratamientos previos, recomendaciones médicas, cirugías..."
              className="border-slate-200 min-h-[100px]"
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Qué incluye cada enfermedad de la pantalla 3, con una explicación corta para
 * que el profesional (o la persona, en el link de autocarga) sepa qué marcar.
 * Criterio ACSM 2015: enfermedad cardiovascular (cardíaca, vascular periférica
 * o cerebrovascular), metabólica (diabetes tipo 1 y 2) o renal.
 */
const ENFERMEDADES_DECLARADAS = [
  {
    id: 'cardiac_disease',
    label: 'Enfermedad cardíaca conocida',
    terminos: [
      ['Infarto de miocardio', 'Se tapó una arteria del corazón y se dañó parte del músculo. Cuenta aunque haya sido hace años.'],
      ['Angina de pecho', 'Dolor u opresión en el pecho con el esfuerzo o el estrés, porque al corazón le llega poca sangre.'],
      ['Stent, angioplastia o bypass', 'Se destaparon o se puentearon arterias del corazón. Cuenta aunque hoy la persona se sienta bien.'],
      ['Insuficiencia cardíaca', 'El corazón no bombea con la fuerza suficiente. Suele dar cansancio, falta de aire e hinchazón de tobillos.'],
      ['Arritmia', 'Ritmo del corazón irregular, muy rápido o muy lento, diagnosticado por un médico (por ejemplo, fibrilación auricular).'],
      ['Valvulopatía', 'Una válvula del corazón no abre o no cierra bien (estenosis o insuficiencia valvular).'],
      ['Marcapasos o cardiodesfibrilador', 'Aparato implantado que regula el ritmo del corazón o corta arritmias graves.'],
      ['Miocardiopatía', 'El músculo del corazón está engrosado, dilatado o rígido.'],
      ['Cardiopatía congénita', 'Malformación del corazón de nacimiento, operada o no.'],
    ],
  },
  {
    id: 'cerebrovascular_disease',
    label: 'Accidente cerebrovascular o AIT',
    terminos: [
      ['ACV (accidente cerebrovascular)', 'Se tapó o se rompió una arteria del cerebro. Puede haber dejado secuelas en la fuerza, el habla o el equilibrio.'],
      ['AIT (accidente isquémico transitorio)', 'Síntomas de ACV que duran minutos y se van solos. Es un aviso de riesgo alto aunque no deje secuelas.'],
    ],
  },
  {
    id: 'peripheral_artery_disease',
    label: 'Enfermedad arterial periférica o aneurisma de aorta',
    terminos: [
      ['Enfermedad arterial periférica', 'Arterias de las piernas estrechadas. Da dolor en las pantorrillas al caminar, que calma al parar (claudicación).'],
      ['Aneurisma de aorta', 'Dilatación de la arteria aorta, en el abdomen o el tórax. El esfuerzo intenso sube la presión sobre esa pared debilitada.'],
    ],
  },
  {
    id: 'chronic_kidney_disease',
    label: 'Enfermedad renal crónica',
    terminos: [
      ['Enfermedad renal crónica', 'Los riñones filtran menos de lo normal desde hace más de 3 meses. Es frecuente en personas con diabetes o hipertensión.'],
      ['Diálisis o trasplante renal', 'Cuentan siempre como enfermedad renal, aunque el trasplante funcione bien.'],
    ],
  },
];

const GLOSARIO_DIABETES = [
  ['Diabetes tipo 1', 'El cuerpo no produce insulina. Se trata siempre con insulina y suele aparecer de joven.'],
  ['Diabetes tipo 2', 'La insulina no alcanza o no funciona bien. Es la más frecuente en adultos; se trata con dieta, pastillas o insulina.'],
  ['Prediabetes', 'No va acá: se marca abajo, en factores de riesgo. No cambia el escenario.'],
];

/**
 * Términos de una enfermedad, cada uno con una explicación de una o dos líneas.
 * `sangria`: alinea el texto con el de la casilla de al lado.
 */
function Glosario({ terminos, sangria = true }) {
  return (
    <dl className={cn('mt-2.5 grid gap-x-5 gap-y-2 sm:grid-cols-2', sangria && 'ml-7')} data-testid="glosario">
      {terminos.map(([termino, explicacion]) => (
        <div key={termino}>
          <dt className="text-xs font-semibold text-slate-700">{termino}</dt>
          <dd className="text-xs text-slate-500 leading-snug">{explicacion}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Step6RiskFactors({ formData, updateField }) {
  return (
    <div className="space-y-5" data-testid="step6-risk-factors">
      {/* Enfermedades diagnosticadas — bloque superior, entrada del motor de decisión */}
      <div className="p-5 border border-slate-200 rounded-lg bg-white space-y-4" data-testid="block-declared-diseases">
        <div>
          <p className="font-semibold text-slate-800">Enfermedades diagnosticadas</p>
          <p className="text-xs text-slate-500 mt-1">
            Determinan la conducta. Marque las diagnosticadas por un médico.
          </p>
        </div>
        <div className="space-y-2">
          {ENFERMEDADES_DECLARADAS.map((d) => {
            const checked = formData[d.id] === true;
            return (
              <div
                key={d.id}
                className={cn(
                  'p-3 border rounded-lg transition-colors',
                  checked ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200 hover:bg-slate-50'
                )}
                data-testid={`item-${d.id}`}
              >
                <label className="flex items-start gap-3 cursor-pointer" data-testid={`label-${d.id}`}>
                  <Checkbox
                    id={d.id}
                    checked={checked}
                    onCheckedChange={(v) => updateField(d.id, v === true)}
                    className="mt-0.5"
                    data-testid={`checkbox-${d.id}`}
                  />
                  <span className="font-medium text-slate-800 text-sm">{d.label}</span>
                </label>
                {/* Fuera del label: leer la explicación no marca la casilla. */}
                <Glosario terminos={d.terminos} />
              </div>
            );
          })}

          {/* Diabetes: mismo control y mismo campo que antes; solo cambió de lugar */}
          <div
            className={cn(
              'p-3 border rounded-lg space-y-3 transition-colors',
              formData.diabetes === true ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200'
            )}
            data-testid="item-diabetes"
          >
            <p className="font-medium text-slate-800 text-sm">
              ¿Tiene diagnóstico médico de diabetes tipo 1 o tipo 2?
            </p>
            <YesNoToggle
              value={formData.diabetes}
              onChange={(v) => updateField('diabetes', v)}
              id="diabetes"
            />
        
            <Glosario terminos={GLOSARIO_DIABETES} sangria={false} />

            {formData.diabetes === true && (
              <div className="pt-3 border-t border-slate-100 space-y-4">
                {/* Required: Treatment status */}
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">
                    ¿Se encuentra en tratamiento? <span className="text-red-500">*</span>
                  </Label>
                  <YesNoToggle
                    value={formData.diabetes_treatment}
                    onChange={(v) => updateField('diabetes_treatment', v)}
                    id="diabetes_treatment"
                  />
                </div>
            
                {/* Type field (always visible when diabetes=Yes) */}
                <div className="space-y-2">
                  <Label className="text-slate-600 text-sm">Tipo (1 / 2 / otra)</Label>
                  <Input
                    value={formData.diabetes_type || ''}
                    onChange={(e) => updateField('diabetes_type', e.target.value)}
                    placeholder="Ej: Tipo 2"
                    className="border-slate-200"
                    data-testid="input-diabetes_type"
                  />
                </div>
            
                {/* Medication field (only if in treatment) */}
                {formData.diabetes_treatment === true && (
                  <div className="space-y-2">
                    <Label className="text-slate-600 text-sm">Medicación actual (opcional)</Label>
                    <Input
                      value={formData.diabetes_medication || ''}
                      onChange={(e) => updateField('diabetes_medication', e.target.value)}
                      placeholder="Ej: Metformina 850mg..."
                      className="border-slate-200"
                      data-testid="input-diabetes_medication"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Factores de riesgo — se registran en el informe, no cambian el escenario */}
      <div className="p-5 border border-slate-200 rounded-lg bg-slate-50/60 space-y-4" data-testid="block-risk-factors">
        <div>
          <p className="font-semibold text-slate-800">Factores de riesgo</p>
          <p className="text-xs text-slate-500 mt-1">
            Se registran en el informe. No modifican el escenario.
          </p>
        </div>
        {/* 1. Tabaquismo */}
        <div className="p-5 border border-slate-200 rounded-lg bg-white">
          <p className="font-medium text-slate-800 mb-3">
            ¿Fuma actualmente o dejó de fumar en los últimos 6 meses?
          </p>
          <YesNoToggle
            value={formData.smoking}
            onChange={(v) => updateField('smoking', v)}
            id="smoking"
          />
        </div>

        {/* 2. Hipertensión Arterial */}
        <div className="p-5 border border-slate-200 rounded-lg bg-white space-y-4">
          <p className="font-medium text-slate-800">
            ¿Tiene diagnóstico médico de hipertensión o registros elevados en reposo?
          </p>
          <YesNoToggle
            value={formData.hypertension}
            onChange={(v) => updateField('hypertension', v)}
            id="hypertension"
          />
        
          {formData.hypertension === true && (
            <div className="pt-3 border-t border-slate-100 space-y-4">
              {/* Required: Treatment status */}
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">
                  ¿Se encuentra en tratamiento? <span className="text-red-500">*</span>
                </Label>
                <YesNoToggle
                  value={formData.hypertension_treatment}
                  onChange={(v) => updateField('hypertension_treatment', v)}
                  id="hypertension_treatment"
                />
              </div>
            
              {/* Optional: Medication (only if in treatment) */}
              {formData.hypertension_treatment === true && (
                <div className="space-y-2">
                  <Label className="text-slate-600 text-sm">Medicación antihipertensiva actual (opcional)</Label>
                  <Input
                    value={formData.hypertension_medication || ''}
                    onChange={(e) => updateField('hypertension_medication', e.target.value)}
                    placeholder="Ej: Losartán 50mg, Amlodipina 5mg..."
                    className="border-slate-200"
                    data-testid="input-hypertension_medication"
                  />
                </div>
              )}
            
              <div className="text-xs text-slate-500 space-y-1">
                <p className="font-medium text-slate-600">Clasificación orientativa:</p>
                <ul className="list-disc list-inside pl-1 space-y-0.5">
                  <li>Normal: &lt;120 y &lt;80 mmHg</li>
                  <li>Elevada: 120–129 y &lt;80 mmHg</li>
                  <li>Estadio 1: 130–139 o 80–89 mmHg</li>
                  <li>Estadio 2: ≥140 o ≥90 mmHg</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Prediabetes: informativa. Con diabetes declarada arriba no aplica. */}
        {formData.diabetes !== true && (
          <div className="p-5 border border-slate-200 rounded-lg bg-white" data-testid="item-prediabetes">
            <p className="font-medium text-slate-800 mb-1">
              ¿Tiene diagnóstico de prediabetes o glucemia alterada en ayunas?
            </p>
            <p className="text-sm text-slate-500 mb-3">
              Glucemia en ayunas entre 100 y 125 mg/dl, o hemoglobina glicosilada (HbA1c) entre 5,7 y 6,4 %.
            </p>
            <YesNoToggle
              value={formData.prediabetes}
              onChange={(v) => updateField('prediabetes', v)}
              id="prediabetes"
            />
          </div>
        )}

        {/* 4. Dislipemia */}
        <div className="p-5 border border-slate-200 rounded-lg bg-white space-y-4">
          <p className="font-medium text-slate-800">
            ¿Tiene diagnóstico médico de colesterol o triglicéridos elevados?
          </p>
          <YesNoToggle
            value={formData.dyslipidemia}
            onChange={(v) => updateField('dyslipidemia', v)}
            id="dyslipidemia"
          />
        
          {formData.dyslipidemia === true && (
            <div className="pt-3 border-t border-slate-100 space-y-3">
              <div className="space-y-2">
                <Label className="text-slate-600 text-sm">¿Se encuentra en tratamiento?</Label>
                <YesNoToggle
                  value={formData.dyslipidemia_treatment}
                  onChange={(v) => updateField('dyslipidemia_treatment', v)}
                  id="dyslipidemia_treatment"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600 text-sm">Medicación hipolipemiante actual (opcional)</Label>
                <Input
                  value={formData.dyslipidemia_medication || ''}
                  onChange={(e) => updateField('dyslipidemia_medication', e.target.value)}
                  placeholder="Ej: Atorvastatina 20mg, Rosuvastatina 10mg..."
                  className="border-slate-200"
                  data-testid="input-dyslipidemia_medication"
                />
              </div>
            </div>
          )}
        </div>

        {/* 5. Antecedentes Familiares */}
        <div className="p-5 border border-slate-200 rounded-lg bg-white">
          <p className="font-medium text-slate-800 mb-1">
            ¿Antecedente de infarto, muerte súbita o revascularización en familiar de primer grado?
          </p>
          <p className="text-sm text-slate-500 mb-3">(Hombres &lt;55 años, Mujeres &lt;65 años)</p>
          <YesNoToggle
            value={formData.family_history}
            onChange={(v) => updateField('family_history', v)}
            id="family_history"
          />
        </div>
      </div>

      {/* Validation message */}
      {(formData.smoking === null || formData.hypertension === null || 
        formData.diabetes === null || formData.dyslipidemia === null || 
        (formData.diabetes !== true && formData.prediabetes === null) ||
        formData.family_history === null) && (
        <p className="text-sm text-amber-600 text-center p-3 bg-amber-50 rounded-lg border border-amber-200" data-testid="risk-factors-warning">
          Respondé todas las preguntas de Sí o No para continuar
        </p>
      )}
    </div>
  );
}

// Va aparte de Step6RiskFactors porque ese paso también lo usan el link del
// alumno y la demo: esta pregunta es solo de la entrevista del profesional.
function PreguntaBetabloqueante({ formData, updateField }) {
  return (
    <div className="mt-4 p-5 border border-slate-200 rounded-lg bg-white" data-testid="pregunta-betabloqueante">
      <p className="font-medium text-slate-800 mb-1">
        ¿Toma algún betabloqueante? <span className="text-red-500">*</span>
      </p>
      <p className="text-sm text-slate-500 mb-3">
        Atenolol, bisoprolol, carvedilol, metoprolol, nebivolol, propranolol. Se indican por
        hipertensión, arritmia, angina, insuficiencia cardíaca, migraña o ansiedad. Con Sí, la
        intensidad se controla por percepción de esfuerzo y test del habla, no por frecuencia cardíaca.
      </p>
      <YesNoToggle
        value={formData.betabloqueante}
        onChange={(v) => updateField('betabloqueante', v)}
        id="betabloqueante"
      />
    </div>
  );
}

function Step7FinalReport({ formData }) {
  // ========== MOTOR + PRESENTACIÓN (mismo helper que el informe final) ==========
  const motorOutput = evaluateDecisionFE(formData);
  // Qué intensidades habilita el escenario. Recién terminada la entrevista no
  // hay apto cargado: se sigue solo el escenario (mismo criterio que el detalle
  // de la entrevista y el PDF).
  const unlock = intensityUnlock(motorOutput.estado);
  const presentationBase = getDecisionPresentation(motorOutput.estado);
  // Override de conducta operativa cuando el motor lo emite (e.g. HTA sin tto + moderado)
  const presentation = motorOutput.conducta_override
    ? { ...presentationBase, conducta: motorOutput.conducta_override }
    : presentationBase;
  const motivos = motorOutput.motivos_decision;

  // ========== CÁLCULOS COMPLEMENTARIOS (IMC, zonas, adiposidad) ==========
  const imc = calculateIMCSafe(formData.weight, formData.height);
  const ageValue = parseSafeNumber(formData.age);
  const waistHeightRatio = calculateWaistHeightRatioSafe(formData.waist_circumference, formData.height);
  const adiposityStatus = evaluateAdiposityStatus(
    formData.waist_circumference,
    formData.height,
    formData.sex,
    formData.weight
  );

  const waistValue = parseSafeNumber(formData.waist_circumference);
  const waistNotAvailable = waistValue === null || waistValue === 0;

  // FC zones (Karvonen)
  const restingHR = parseSafeNumber(formData.resting_heart_rate);
  const fcPicoPEG = parseSafeNumber(formData.stress_test_fc_pico);
  const hrZones = calculateHeartRateZonesSafe(parseSafeNumber(ageValue, 0), restingHR, fcPicoPEG);

  // Información clínica relevante (mismo helper que EvaluationDetailPage)
  const clinicalInfo = buildClinicalInfo({
    ...formData,
    imc: parseSafeNumber(imc),
    waist_height_ratio: parseSafeNumber(waistHeightRatio),
    adiposity_status: adiposityStatus
      ? (adiposityStatus.status === 'normal' ? 'normal' : 'elevated_central')
      : null,
  });

  // Mapeo de color del banner → clases tailwind para el banner principal del preview
  const bannerHero = {
    rojo: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-900', accent: 'text-red-700' },
    naranja: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-900', accent: 'text-orange-700' },
    amarillo: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-900', accent: 'text-amber-700' },
    amarillo_claro: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', accent: 'text-yellow-700' },
    verde: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', accent: 'text-green-700' },
  }[presentation.bannerColor] || { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', accent: 'text-slate-500' };

  return (
    <div className="space-y-6" data-testid="step7-final-report">
      {/* === Banner principal: driven by motor estado === */}
      <p
        className="text-xs sm:text-sm text-slate-600 text-center mb-2"
        data-testid="step7-decision-preheader"
      >
        Según los datos recabados en la entrevista:
      </p>
      <div
        className={cn('p-6 rounded-lg border-2', bannerHero.bg, bannerHero.border)}
        data-testid="step7-decision-banner"
      >
        <div className="flex justify-center mb-4">
          <img src="/logo-institucional.png" alt="Ejercicio Físico y Salud" className="h-24 w-auto object-contain" />
        </div>
        <p className={cn('text-xs font-medium uppercase tracking-wider text-center mb-2', bannerHero.accent)}>
          Recomendación operativa
        </p>
        <h2
          className={cn('text-xl font-bold tracking-tight text-center leading-tight', bannerHero.text)}
          data-testid="step7-decision-headline"
        >
          {presentation.headline}
        </h2>
        <p className="text-xs text-slate-400 mt-3 text-center">
          Fecha de entrevista: {new Date().toLocaleDateString('es-AR', {
            day: '2-digit', month: '2-digit', year: 'numeric'
          })}
        </p>
      </div>

      {/* === Motivo de la recomendación === */}
      <Card className="border-slate-200" data-testid="step7-motivo-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-['Manrope'] text-slate-700">
            Motivo de la recomendación
          </CardTitle>
        </CardHeader>
        <CardContent>
          {motivos && motivos.length > 0 ? (
            <ul className="space-y-2 text-sm text-slate-700" data-testid="step7-motivos-list">
              {motivos.map((m, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-slate-400" aria-hidden="true">•</span>
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-400 italic">Sin motivos disponibles.</p>
          )}
        </CardContent>
      </Card>

      {/* === Recomendaciones operativas (moderado / vigoroso) === */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-['Manrope'] text-slate-700">Recomendaciones</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className={cn('p-3 rounded-lg border', bannerHero.bg, bannerHero.border)}>
            <p className="text-[10px] uppercase tracking-wider opacity-70">Ejercicio leve a moderado</p>
            <p className={cn('text-sm font-semibold mt-1', bannerHero.text)} data-testid="step7-rec-moderate">
              {presentation.moderate}
            </p>
          </div>
          <div className={cn('p-3 rounded-lg border', bannerHero.bg, bannerHero.border)}>
            <p className="text-[10px] uppercase tracking-wider opacity-70">Ejercicio vigoroso</p>
            <p className={cn('text-sm font-semibold mt-1', bannerHero.text)} data-testid="step7-rec-vigorous">
              {presentation.vigorous}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* === Recomendaciones de fuerza === */}
      <Card className="border-slate-200" data-testid="step7-strength-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-['Manrope'] text-slate-700">
            Recomendaciones de fuerza
          </CardTitle>
        </CardHeader>
        <CardContent>
          <StrengthBlock estado={motorOutput.estado} />
        </CardContent>
      </Card>

      {/* === Conducta operativa === */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-['Manrope'] text-slate-700">
            Conducta operativa
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={cn('p-3 rounded-lg border', bannerHero.bg, bannerHero.border)}>
            <p className={cn('text-sm font-medium', bannerHero.text)} data-testid="step7-conducta-text">
              {presentation.conducta}
            </p>
          </div>
          <p
            className="mt-2 text-[10px] italic text-slate-400"
            data-testid="step7-conducta-citation"
          >
            Basado en algoritmo de screening preparticipativo ACSM (Riebe et al., 2015) y guías AHA/ESC.
          </p>
        </CardContent>
      </Card>

      {/* === Información clínica relevante === */}
      <Card className="border-slate-200" data-testid="step7-clinical-info">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-['Manrope'] text-slate-700">
            Información clínica relevante
          </CardTitle>
          <p className="text-xs text-slate-400 mt-0.5 italic">
            Datos no decisorios — para consideración profesional
          </p>
        </CardHeader>
        <CardContent>
          {clinicalInfo.length === 0 ? (
            <p className="text-sm text-slate-400 italic">Sin observaciones contextuales.</p>
          ) : (
            <ul className="space-y-2">
              {clinicalInfo.map((it, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="text-slate-400 mt-0.5" aria-hidden="true">•</span>
                  <span className="flex-1 break-words">
                    {it.label}
                    {it.value && <span className="font-semibold ml-1">{it.value}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* === Zonas Cardiorrespiratorias: lo que el escenario no habilita va en gris, con el motivo === */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-['Manrope'] text-slate-700">
            {formData.betabloqueante === true ? 'Control de intensidad' : 'Zonas de Entrenamiento Cardiorrespiratorio'}
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            {formData.betabloqueante === true
              ? '(Percepción de esfuerzo — escala de Borg 6-20 — y test del habla)'
              : '(% de Frecuencia Cardíaca de Reserva — Método Karvonen)'}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.betabloqueante === true ? (
            <ControlSinFC unlock={unlock} />
          ) : hrZones.insufficientData ? (
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-slate-600 text-center">Datos insuficientes para calcular zonas de frecuencia cardíaca</p>
              <p className="text-xs text-slate-400 text-center mt-1">Se requiere al menos la edad para el cálculo</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { key: 'light', label: 'Zona Leve (30–39% FCR)', low: hrZones.lightLow, high: hrZones.lightHigh,
                    box: 'bg-sky-50 border-sky-200', title: 'text-sky-600', value: 'text-sky-800' },
                  { key: 'moderate', label: 'Zona Moderada (40–59% FCR)', low: hrZones.moderateLow, high: hrZones.moderateHigh,
                    box: 'bg-emerald-50 border-emerald-200', title: 'text-emerald-600', value: 'text-emerald-800' },
                  { key: 'vigorous', label: 'Zona Vigorosa (60–89% FCR)', low: hrZones.vigorousLow, high: hrZones.vigorousHigh,
                    box: 'bg-amber-50 border-amber-200', title: 'text-amber-600', value: 'text-amber-800' },
                ].map((z) => (unlock[z.key].enabled ? (
                  <div key={z.key} className={`p-4 rounded-xl border overflow-hidden ${z.box}`} data-testid={`wizard-zone-${z.key}-enabled`}>
                    <p className={`text-xs font-medium uppercase tracking-wider mb-1 ${z.title}`}>{z.label}</p>
                    <p className={`text-xl sm:text-2xl font-bold ${z.value}`}>{z.low} – {z.high} <span className="text-sm font-normal">lpm</span></p>
                  </div>
                ) : (
                  <div key={z.key} className="p-4 rounded-xl border border-gray-200 bg-gray-50 opacity-70 overflow-hidden" data-testid={`wizard-zone-${z.key}-blocked`}>
                    <p className="text-xs font-medium uppercase tracking-wider mb-1 text-gray-500">{z.label}</p>
                    <p className="text-xs italic text-gray-500 leading-relaxed">{unlock[z.key].reason}</p>
                  </div>
                )))}
              </div>
              <p className="text-xs text-slate-400">
                FC máxima: {hrZones.maxHR !== null ? Math.round(hrZones.maxHR) : '—'} lpm {hrZones.usedPEG ? '(PEG)' : '(Fórmula Tanaka)'} | {hrZones.restingHRAssumed ? 'FC reposo asumida: 70 lpm (valor estándar, no medido)' : `FC reposo: ${hrZones.restingHR} lpm`}
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* === Datos del Evaluado === */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-['Manrope'] text-slate-700">Datos del Evaluado</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between py-1.5 border-b border-slate-100">
            <span className="text-slate-500">Nombre</span>
            <span className="font-medium text-slate-800">{formData.student_name}</span>
          </div>
          <div className="flex justify-between py-1.5 border-b border-slate-100">
            <span className="text-slate-500">Edad</span>
            <span className="font-medium text-slate-800">{formData.age} años</span>
          </div>
          <div className="flex justify-between py-1.5 border-b border-slate-100">
            <span className="text-slate-500">Género</span>
            <span className="font-medium text-slate-800">{formData.sex}</span>
          </div>
          <div className="flex justify-between py-1.5 border-b border-slate-100">
            <span className="text-slate-500">IMC</span>
            <span className="font-medium text-slate-800">{imc} kg/m²</span>
          </div>
          <div className="flex justify-between py-1.5">
            <span className="text-slate-500">Nivel de Actividad</span>
            <span className="font-medium text-slate-800">{formatActivityLevel(formData.activity_level)}</span>
          </div>
        </CardContent>
      </Card>

      {/* === Adiposidad Corporal === */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-['Manrope'] text-slate-700">Evaluación de Adiposidad Corporal</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 text-sm">
            <div className="p-3 bg-slate-50 rounded-xl">
              <p className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Peso</p>
              <p className="font-semibold text-slate-800">{formData.weight} kg</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-xl">
              <p className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Altura</p>
              <p className="font-semibold text-slate-800">{formData.height} cm</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-xl">
              <p className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">IMC</p>
              <p className="font-semibold text-slate-800">{imc} kg/m²</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-xl">
              <p className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Cintura</p>
              <p className="font-semibold text-slate-800 break-words">
                {waistNotAvailable ? <span className="text-slate-400 italic font-normal text-xs">no disponible</span> : `${formData.waist_circumference} cm`}
              </p>
            </div>
            {!waistNotAvailable && waistHeightRatio && (
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Cintura/Altura</p>
                <p className="font-semibold text-slate-800">{waistHeightRatio}</p>
              </div>
            )}
          </div>

          {!waistNotAvailable && adiposityStatus && (
            <div className={cn(
              'p-4 rounded-xl border',
              adiposityStatus.status === 'normal'
                ? 'bg-green-50 border-green-200'
                : 'bg-amber-50 border-amber-200'
            )}>
              <p className={cn(
                'text-sm font-medium leading-relaxed break-words',
                adiposityStatus.status === 'normal' ? 'text-green-700' : 'text-amber-700'
              )}>
                {adiposityStatus.description}
              </p>
              {adiposityStatus.secondaryDescription && (
                <p className={cn(
                  'text-sm mt-2 leading-relaxed break-words',
                  adiposityStatus.status === 'normal' ? 'text-green-700' : 'text-amber-700'
                )}>
                  {adiposityStatus.secondaryDescription}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* === Estudios Complementarios === */}
      {(formData.has_lab_recent || formData.has_ecg || formData.has_stress_test) && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-['Manrope'] text-slate-700">Estudios Complementarios</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {formData.has_lab_recent && <span className="text-xs bg-slate-100 text-slate-700 px-3 py-1 rounded-full">Laboratorio</span>}
              {formData.has_ecg && <span className="text-xs bg-slate-100 text-slate-700 px-3 py-1 rounded-full">ECG</span>}
              {formData.has_stress_test && <span className="text-xs bg-slate-100 text-slate-700 px-3 py-1 rounded-full">Ergometría</span>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* === Disclaimer legal operativo === */}
      <div className="p-4 bg-slate-100 border border-slate-200 rounded-lg space-y-2">
        <p className="text-xs text-slate-500 leading-relaxed">
          <strong>Aviso legal:</strong> Esta herramienta es orientativa y no reemplaza valoración médica.
          La interpretación y prescripción final es responsabilidad del profesional. Tratamiento de datos conforme Ley 25.326.
        </p>
        <p className="text-[11px] text-slate-500 italic leading-relaxed">
          Esta entrevista es un protocolo operativo de detección preliminar realizado por profesional de la actividad física.
          No constituye diagnóstico médico ni reemplaza la evaluación de un profesional de la salud. La decisión final de
          iniciar, continuar o suspender un programa de ejercicio es responsabilidad compartida entre el profesional, el
          alumno y el médico tratante cuando corresponda.
        </p>
      </div>

      {/* === Footer branding === */}
      <div className="pt-6 mt-6 border-t border-slate-200">
        <div className="text-center">
          <p className="text-base font-bold text-[#1F3A5F] font-['Inter'] tracking-wider">EVALPRO</p>
          <p className="text-xs text-[#1F3A5F]/60 font-['Inter']">Sistema de Entrevista Profesional</p>
          <p className="text-sm font-medium text-[#1F3A5F] font-['Inter'] mt-1">Ejercicio Físico y Salud</p>
          <p className="text-xs text-slate-400 mt-1">@ejerciciofisicoysaludok</p>
        </div>
      </div>
    </div>
  );
}
